import { useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@sportscanner/ui";
import { inviteSchema } from "@sportscanner/schemas";
import { useMe, useSubscriptionActive } from "@/lib/me";
import { apiPost, getApiErrorCode } from "@/lib/api";
import ClubAutocomplete from "@/components/club-autocomplete";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function InviteSuccess({
  email,
  onSendAnother,
}: {
  email: string;
  onSendAnother: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7 text-primary-500" />
      </div>
      <div>
        <p className="font-semibold text-neutral-800">
          {t("invite.successHeading")}
        </p>
        <p className="text-sm text-neutral-500 mt-1">
          {t("invite.successMessage", { email })}
        </p>
      </div>
      <button
        onClick={onSendAnother}
        className="text-sm text-primary-600 hover:underline cursor-pointer"
      >
        {t("invite.sendAnother")}
      </button>
    </div>
  );
}

function ManagerInviteForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [clubName, setClubName] = useState("");
  const [clubSlug, setClubSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  // Stored as a string while editing so the field can be cleared without
  // snapping to 0. Empty string = unlimited.
  const [maxCourtsInput, setMaxCourtsInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  function handleClubNameChange(value: string) {
    setClubName(value);
    if (!slugEdited) {
      setClubSlug(slugify(value));
    }
  }

  const maxCourtsNum = maxCourtsInput.trim()
    ? Number.parseInt(maxCourtsInput, 10)
    : null;
  const maxCourtsInvalid =
    maxCourtsNum !== null &&
    (!Number.isFinite(maxCourtsNum) || maxCourtsNum < 1 || maxCourtsNum > 1000);

  const isDisabled =
    !email.trim() ||
    (!clubName.trim() && !clubSlug.trim()) ||
    isLoading ||
    maxCourtsInvalid;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = inviteSchema.safeParse({
      email: email.trim(),
      clubName: clubName.trim() || undefined,
      clubSlug: clubSlug.trim() || undefined,
      maxCourts: maxCourtsNum,
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    try {
      await apiPost("/api/admin/invite", parsed.data);
      setSuccessEmail(email.trim());
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setIsLoading(false);
    }
  }

  if (successEmail) {
    return (
      <InviteSuccess
        email={successEmail}
        onSendAnother={() => {
          setSuccessEmail(null);
          setEmail("");
          setClubName("");
          setClubSlug("");
          setSlugEdited(false);
          setMaxCourtsInput("");
        }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="manager-email" className="text-sm text-neutral-600">
          {t("invite.emailLabel")} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="manager-email"
          type="email"
          placeholder={t("invite.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          className="rounded-lg border-neutral-200 focus:border-primary-500"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="club-name" className="text-sm text-neutral-600">
          {t("invite.clubNameLabel")} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="club-name"
          type="text"
          placeholder={t("invite.clubNamePlaceholder")}
          value={clubName}
          onChange={(e) => handleClubNameChange(e.target.value)}
          disabled={isLoading}
          className="rounded-lg border-neutral-200 focus:border-primary-500"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="club-slug" className="text-sm text-neutral-600">
          {t("invite.clubSlugLabel")} <span className="text-red-500">*</span>
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-neutral-400 shrink-0">
            sportscanner.com/
          </span>
          <Input
            id="club-slug"
            type="text"
            placeholder={t("invite.clubSlugPlaceholder")}
            value={clubSlug}
            onChange={(e) => {
              setClubSlug(e.target.value);
              setSlugEdited(true);
            }}
            disabled={isLoading}
            className="rounded-lg border-neutral-200 focus:border-primary-500 flex-1 min-w-[200px]"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="club-max-courts" className="text-sm text-neutral-600">
          {t("invite.maxCourtsLabel")}
        </Label>
        <p className="text-xs text-neutral-500">{t("invite.maxCourtsHint")}</p>
        <Input
          id="club-max-courts"
          type="number"
          inputMode="numeric"
          min={1}
          max={1000}
          step={1}
          placeholder={t("invite.maxCourtsPlaceholder")}
          value={maxCourtsInput}
          onChange={(e) => setMaxCourtsInput(e.target.value)}
          disabled={isLoading}
          aria-invalid={maxCourtsInvalid}
          className={`rounded-lg border-neutral-200 focus:border-primary-500 w-40 ${
            maxCourtsInvalid ? "border-red-400" : ""
          }`}
        />
        {maxCourtsInvalid && (
          <p className="text-xs text-red-500">
            {t("errors.api.MAX_COURTS_INVALID")}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isDisabled}
        className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("invite.submitting")}
          </>
        ) : (
          t("invite.submitManager")
        )}
      </Button>
    </form>
  );
}

const PERMISSION_KEYS = [
  "view_bookings",
  "create_booking",
  "cancel_booking",
  "edit_booking",
  "manage_blocks",
  "manage_closures",
  "view_revenue",
  "override_booking_limits",
] as const;
type PermissionKey = (typeof PERMISSION_KEYS)[number];

// Mirrors STAFF_PERMISSION_DEFAULTS in apps/api/src/lib/permissions.ts. The
// backend re-applies these defaults if the client omits a key, so the UI is
// strictly a convenience.
const STAFF_PERMISSION_DEFAULTS: Record<PermissionKey, boolean> = {
  view_bookings: true,
  create_booking: true,
  cancel_booking: true,
  edit_booking: false,
  manage_blocks: true,
  manage_closures: false,
  view_revenue: false,
  override_booking_limits: false,
};

function StaffInviteForm({
  requireClubPicker,
}: {
  requireClubPicker: boolean;
}) {
  const { t } = useTranslation();
  const subscriptionActive = useSubscriptionActive();
  const [email, setEmail] = useState("");
  const [clubId, setClubId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<
    Record<PermissionKey, boolean>
  >({ ...STAFF_PERMISSION_DEFAULTS });
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const isDisabled =
    !email.trim() ||
    (requireClubPicker && !clubId) ||
    isLoading ||
    !subscriptionActive;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = inviteSchema.safeParse({
      email: email.trim(),
      permissions,
      ...(requireClubPicker
        ? { role: "STAFF", clubId: clubId ?? undefined }
        : {}),
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    try {
      await apiPost("/api/admin/invite", parsed.data);
      setSuccessEmail(email.trim());
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setIsLoading(false);
    }
  }

  if (successEmail) {
    return (
      <InviteSuccess
        email={successEmail}
        onSendAnother={() => {
          setSuccessEmail(null);
          setEmail("");
          setClubId(null);
          setPermissions({ ...STAFF_PERMISSION_DEFAULTS });
        }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="staff-email" className="text-sm text-neutral-600">
          {t("invite.emailLabel")} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="staff-email"
          type="email"
          placeholder={t("invite.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          className="rounded-lg border-neutral-200 focus:border-primary-500"
        />
      </div>

      {requireClubPicker && (
        <div className="space-y-2">
          <Label className="text-sm text-neutral-600">
            {t("autocomplete.pickClubLabel")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <ClubAutocomplete
            value={clubId}
            onChange={setClubId}
            disabled={isLoading}
          />
        </div>
      )}

      <div className="space-y-3 pt-2 border-t border-neutral-100">
        <div className="pt-3">
          <h3 className="text-sm font-medium text-neutral-700">
            {t("invite.permissionsTitle")}
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            {t("invite.permissionsHelp")}
          </p>
        </div>
        <div className="divide-y divide-neutral-100">
          {PERMISSION_KEYS.map((key) => (
            <div
              key={key}
              className="flex items-start justify-between gap-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <Label
                  htmlFor={`perm-${key}`}
                  className="text-sm font-medium text-neutral-800 cursor-pointer"
                >
                  {t(`invite.permissions.${key}.label`)}
                </Label>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {t(`invite.permissions.${key}.description`)}
                </p>
              </div>
              <Switch
                id={`perm-${key}`}
                checked={permissions[key]}
                onCheckedChange={(checked) =>
                  setPermissions((prev) => ({ ...prev, [key]: checked }))
                }
                disabled={isLoading}
              />
            </div>
          ))}
        </div>
      </div>

      <Button
        type="submit"
        disabled={isDisabled}
        className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("invite.submitting")}
          </>
        ) : (
          t("invite.submitStaff")
        )}
      </Button>
    </form>
  );
}

function CoachInviteForm({
  requireClubPicker,
}: {
  requireClubPicker: boolean;
}) {
  const { t } = useTranslation();
  const subscriptionActive = useSubscriptionActive();
  const [email, setEmail] = useState("");
  const [clubId, setClubId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const isDisabled =
    !email.trim() ||
    (requireClubPicker && !clubId) ||
    isLoading ||
    !subscriptionActive;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = inviteSchema.safeParse({
      email: email.trim(),
      role: "COACH",
      ...(requireClubPicker ? { clubId: clubId ?? undefined } : {}),
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);
    try {
      await apiPost("/api/admin/invite", parsed.data);
      setSuccessEmail(email.trim());
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setIsLoading(false);
    }
  }

  if (successEmail) {
    return (
      <InviteSuccess
        email={successEmail}
        onSendAnother={() => {
          setSuccessEmail(null);
          setEmail("");
          setClubId(null);
        }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <p className="text-sm text-neutral-500">{t("invite.coachHelp")}</p>

      <div className="space-y-2">
        <Label htmlFor="coach-email" className="text-sm text-neutral-600">
          {t("invite.emailLabel")} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="coach-email"
          type="email"
          placeholder={t("invite.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          className="rounded-lg border-neutral-200 focus:border-primary-500"
        />
      </div>

      {requireClubPicker && (
        <div className="space-y-2">
          <Label className="text-sm text-neutral-600">
            {t("autocomplete.pickClubLabel")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <ClubAutocomplete
            value={clubId}
            onChange={setClubId}
            disabled={isLoading}
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={isDisabled}
        className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("invite.submitting")}
          </>
        ) : (
          t("invite.submitCoach")
        )}
      </Button>
    </form>
  );
}

export default function InvitePage() {
  const { t } = useTranslation();
  const { user } = useMe();
  const role = user?.role || "MANAGER";

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-xl">
      <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-6">
        {t("invite.title")}
      </h1>

      {role === "SUPERADMIN" ? (
        <Tabs defaultValue="manager" className="space-y-6">
          <TabsList className="w-full">
            <TabsTrigger value="manager" className="flex-1 cursor-pointer">
              {t("invite.managerTab")}
            </TabsTrigger>
            <TabsTrigger value="staff" className="flex-1 cursor-pointer">
              {t("invite.staffTab")}
            </TabsTrigger>
            <TabsTrigger value="coach" className="flex-1 cursor-pointer">
              {t("invite.coachTab")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="manager">
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <ManagerInviteForm />
            </div>
          </TabsContent>
          <TabsContent value="staff">
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <StaffInviteForm requireClubPicker />
            </div>
          </TabsContent>
          <TabsContent value="coach">
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <CoachInviteForm requireClubPicker />
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="staff" className="space-y-6">
          <TabsList className="w-full">
            <TabsTrigger value="staff" className="flex-1 cursor-pointer">
              {t("invite.staffTab")}
            </TabsTrigger>
            <TabsTrigger value="coach" className="flex-1 cursor-pointer">
              {t("invite.coachTab")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="staff">
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <StaffInviteForm requireClubPicker={false} />
            </div>
          </TabsContent>
          <TabsContent value="coach">
            <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
              <CoachInviteForm requireClubPicker={false} />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
